import * as THREE from "three";
import { walkingWith } from "../../sim/backpack";
import { BAIL_TIME, type Skater } from "../../skater";
import type { FlipAnim, GrindStyle } from "../../tricks";
import { setArm } from "../arm";
import { MAX_HIP, MIN_HIP } from "../ik";
import { clamp } from "../pose";
import type { Leg, SkaterLook, SkaterRig } from "../skater-parts";
import { easeBy, footTo, gripWith, Held, heldGrab, RidePose, type RideCue, type RideRig } from "./ride-pose";
import { buildSkates, SKATE_FOOT_Y, SKATE_WHEEL_R, type Skates } from "./skates";

// The rider on roller skates (#140), posed over the board's pose each frame
// by `SkaterModel`, after the scooter's. The skates are worn on the feet
// (skates.ts), and on foot slung over the shoulder by their laces; getting
// on and off and the arm rate limit are the rides' shared `RidePose`. It reads the same eased
// values off the board pose as the bike does (`RideCue`).

export class SkatesPose implements RideRig {
  /** The skates, built the first time someone puts them on; and how far the wheels have turned. */
  private skates: Skates | null = null;
  private wheelTurn = 0;
  private readonly ride: RidePose;
  private crouch = 0;
  /** The grab, eased; switching lets go first. */
  private grab = new Held<string>(18);
  /** The soul plate trick's pose, eased in; the stride's phase and how much it's striding (0..1, eased). */
  private grind = new Held<GrindStyle>(16);
  private stridePhase = 0;
  private stride = 0;
  /** Slung over the shoulder this frame (`walkBeside`). */
  private walked = false;

  constructor(
    private rig: SkaterRig,
    private look: SkaterLook,
  ) {
    this.ride = new RidePose(rig);
  }

  /**
   * On skates (#140): the board's hidden and the rider stands on the skates
   * facing the way they roll, feet side by side. Rolling along uncrouched,
   * they stride: each skate pushes out and back in turn, arms swinging.
   * Airs tuck the knees; grabs reach a hand to a skate (`SKATE_GRABS`); □
   * throws the body round (`SKATE_SPINS`); a soul plate trick turns the
   * rider across the rail (`SOUL_POSES`); a bail sits them down hard.
   * Returns whether it posed the rider (so the model re-reads the arms' raise).
   */
  apply(sk: Skater, dt: number, cue: Readonly<RideCue>): boolean {
    const on = sk.ride === "skates" && sk.mode !== "foot";
    const onW = this.ride.ease(on, dt);
    // Putting them on or taking them off, the worn pair goes between the shoulder and the feet (`fromSling`).
    if (this.skates) {
      for (const s of this.skates.worn) {
        s.visible = onW > 0;
        s.position.set(0, 0, 0);
        s.quaternion.identity();
      }
      this.skates.carried.visible = this.walked && onW === 0;
    }
    if (onW === 0) return false;
    const sks = this.ensureSkates();
    if (on) this.rig.board.visible = false;
    this.ride.saveJoints();
    if (sk.mode === "ground") this.wheelTurn += (sk.speed * dt) / SKATE_WHEEL_R;
    for (const w of sks.wheels) w.rotation.z = -this.wheelTurn;

    // Grabs ease in and out; switching grabs lets go of the last one first.
    this.grab.update(heldGrab(sk), dt);
    this.grind.update(sk.mode === "grind" && sk.grind ? sk.grind.trick.style : null, dt);
    const style = this.grind.name;
    const soul = style ? (SOUL_POSES[style] ?? SOUL_POSES.fiftyfifty!) : null;
    const gw = soul ? this.grind.value : 0;

    // The move in progress: a □ trick in the air, or a hop spun out of a manual.
    const pop = sk.manual?.pop;
    const move = sk.flip
      ? { anim: sk.flip.trick.anim, n: sk.flip.trick.turns, p: Math.min(1, sk.flip.t / sk.flip.duration), hop: false }
      : pop
        ? { anim: pop.trick.anim, n: pop.trick.rotations, p: Math.min(1, pop.t / pop.duration), hop: true }
        : null;
    const tuck = move ? Math.sin(Math.PI * move.p) : 0;

    this.crouch += (sk.crouch - this.crouch) * easeBy(12, dt);
    const crouch = this.crouch;
    const lipping = sk.mode === "lip" && sk.lip !== null;
    let hipY = STAND_HIP - 0.22 * crouch - 0.12 * cue.airTuck - 0.18 * tuck + 0.6 * cue.landDip;
    hipY -= (soul?.crouch ?? 0) * gw + (lipping ? 0.12 : 0);
    // A grab pulls a skate up to the hand: the knees tuck.
    hipY -= 0.18 * (this.part("Mute") + this.part("Japan") + this.part("Stale"));
    hipY = clamp(hipY, MIN_HIP, MAX_HIP);

    // The rider: facing the way the skates point (+z), leaning into turns;
    // across the rail for a soul plate trick, side-on on the coping.
    const yaw = Math.PI / 2 + (soul?.yaw ?? 0) * gw + (lipping ? Math.PI / 2 : 0);
    const riderQ = TMP_Q.setFromAxisAngle(Y_AXIS, yaw);
    riderQ.premultiply(TMP_Q2.setFromAxisAngle(Z_AXIS, -0.5 * cue.lean));
    const at = TMP_V.set(0, SKATE_FOOT_Y + (sk.mode === "grind" ? GRIND_LIFT : 0), 0);
    if (sk.mode === "bail") {
      // Sat down hard: the rider drops back onto the seat of their pants and gets up.
      const fall = Math.min(1, sk.bailT / 0.25);
      const getUp = Math.max(0, (sk.bailT - (BAIL_TIME - 0.35)) / 0.35);
      const p = fall * (1 - getUp);
      riderQ.premultiply(TMP_Q2.setFromAxisAngle(X_AXIS, -1.1 * p));
      at.y -= 0.35 * p;
      at.z -= 0.3 * p;
    }
    this.rig.rider.quaternion.copy(riderQ);
    this.rig.rider.position.copy(at);
    this.rig.hips.position.set(0, hipY, 0);
    this.rig.torso.rotation.set(0, 0, 0.3 + 0.35 * crouch + 0.25 * tuck + (soul?.lean ?? 0) * gw, "YZX");
    this.rig.head.rotation.set(0, 0, -0.2 - 0.2 * crouch, "ZYX");

    // Stride (#140): rolling along on the ground uncrouched, each skate in
    // turn pushes out to its side and back, then swings back in under you.
    const striding = sk.mode === "ground" && !sk.manual && crouch < 0.3 && Math.abs(sk.speed) > 0.5;
    this.stride += ((striding ? 1 : 0) - this.stride) * easeBy(6, dt);
    if (this.stride > 0.001) this.stridePhase = (this.stridePhase + STRIDE_RATE * dt) % (Math.PI * 2);
    else this.stridePhase = 0;
    const legs = this.rig.legs;
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i]!;
      const push = Math.max(0, Math.sin(this.stridePhase + i * Math.PI)) * this.stride;
      let ahead = (i === 0 ? FOOT_STAGGER : -FOOT_STAGGER) - 0.22 * push;
      let out = -leg.z * 0.35 + Math.sign(leg.z) * 0.14 * push;
      if (soul) {
        ahead += (i === 0 ? soul.front : soul.back) * gw;
        out += Math.sign(leg.z) * soul.spread * gw;
      }
      footTo(leg, hipY - 0.03 * push, ahead, out);
    }
    if (soul && gw > 0) {
      // Makio and Topsoul: one foot off the rail or rolled onto its top.
      legs[1]!.hip.rotation.z -= soul.lift * gw;
      legs[1]!.knee.rotation.z += soul.lift * gw;
      legs[0]!.foot.rotation.x += soul.roll * gw;
      // Royale and Unity: the back skate crossed behind the front one.
      legs[1]!.hip.rotation.x -= soul.cross * gw;
    }
    if (lipping && sk.lip!.trick.style === "nose") for (const leg of legs) leg.foot.rotation.z += 0.5;

    // Grabs: a leg kicked out straight (Rocket, both forward; Liu Kang, one),
    // the back skate tweaked behind (Japan, Stale).
    const rocket = this.part("Rocket");
    const liu = this.part("Liu Kang");
    kickForward(legs[0]!, 1.1 * rocket + 1.3 * liu);
    kickForward(legs[1]!, 1.1 * rocket - 0.5 * liu);
    kickForward(legs[1]!, -0.7 * this.part("Japan") - 0.9 * this.part("Stale"));

    // Arms: swinging with the stride, out for balance on a rail or in the air.
    const swing = Math.sin(this.stridePhase) * this.stride;
    const wide = sk.mode === "ground" ? 0.25 : 1;
    if (sk.mode !== "bail") {
      setArm(this.rig.armFront, -1, { out: 0.3 + 0.9 * wide, back: 0.5 * swing, bend: 0.4 });
      setArm(this.rig.armBack, 1, { out: 0.3 + 0.9 * wide, back: -0.5 * swing, bend: 0.4 });
    }
    // A grab reaches a hand down to a skate (the front hand for a Mute, Japan
    // or Rocket, the back hand behind for a Stale); a Rocket takes both.
    const front = this.part("Mute") + this.part("Japan") + rocket;
    const back = this.part("Stale") + rocket;
    if (front > 0 || back > 0) {
      this.rig.root.updateMatrixWorld(true);
      if (front > 0) gripWith(this.rig.armFront, sks.worn[0]!.localToWorld(TMP_V2.set(-0.02, -0.04, 0)), Math.min(1, front), false);
      if (back > 0) gripWith(this.rig.armBack, sks.worn[1]!.localToWorld(TMP_V2.set(0.06, -0.04, 0)), Math.min(1, back), false);
    }
    this.ride.limitArms(sk.mode !== "bail" && onW === 1 && !move, dt);

    // □ throws the whole body round about the hips; a hop out of a manual spins it flat.
    if (move) {
      const turn = Math.PI * 2 * move.n * move.p * (move.hop && move.anim === "varial" ? 1.5 : 1);
      const spin = move.hop ? HOP_SPIN : (SKATE_SPINS[move.anim] ?? SKATE_SPINS.kickflip!);
      spinAboutHips(this.rig, spin, turn, hipY);
    }
    this.ride.blendJoints();
    if (this.ride.between) this.fromSling(sks, onW);
    return true;
  }

  /**
   * On foot with the skates out of the pack (#145): slung over the front
   * shoulder by their laces, one skate down the chest and one down the
   * back, following the shoulder as you walk; the hands stay free. Run
   * before `apply`, as the bike's is, so putting them on starts from here.
   * Returns whether it posed the arm (never: it leaves the arms to the walk).
   */
  walkBeside(sk: Skater, _dt: number, _out: number): boolean {
    this.walked = false;
    if (walkingWith(sk) !== "skates") {
      if (this.skates) this.skates.carried.visible = false;
      return false;
    }
    const sks = this.ensureSkates();
    this.walked = true;
    sks.carried.visible = true;
    const shoulder = this.rig.armFront.shoulder;
    this.rig.root.updateMatrixWorld(true);
    this.rig.yawGroup.worldToLocal(shoulder.getWorldPosition(sks.carried.position));
    sks.carried.position.y += SLING_UP;
    // The skates hang outside the shoulder whichever side it's on.
    const side = Math.sign(shoulder.position.z) || 1;
    sks.carried.rotation.set(0, side > 0 ? 0 : Math.PI, 0);
    this.ride.noteBeside(sks.carried);
    return false;
  }

  /**
   * Putting them on or taking them off (#145): each worn skate is drawn
   * `w` of the way from where it hangs slung to where it's worn on its
   * foot, so they come down off the shoulder onto the feet and go back up.
   */
  private fromSling(sks: Skates, w: number): void {
    this.rig.root.updateMatrixWorld(true);
    for (let i = 0; i < sks.worn.length; i++) {
      const s = sks.worn[i]!;
      sks.carried.children[i]!.matrixWorld.decompose(TMP_V, TMP_Q, TMP_S);
      s.matrixWorld.decompose(TMP_V2, TMP_Q2, TMP_S);
      TMP_V.lerp(TMP_V2, w);
      TMP_Q.slerp(TMP_Q2, w);
      TMP_M.compose(TMP_V, TMP_Q, ONE).premultiply(TMP_M2.copy(s.parent!.matrixWorld).invert());
      TMP_M.decompose(s.position, s.quaternion, TMP_S);
    }
  }

  private ensureSkates(): Skates {
    if (!this.skates) {
      this.skates = buildSkates(this.look);
      this.rig.legs.forEach((leg, i) => leg.foot.add(this.skates!.worn[i]!));
      this.rig.yawGroup.add(this.skates.carried);
      for (const s of this.skates.worn) s.visible = false;
      this.skates.carried.visible = false;
    }
    return this.skates;
  }

  /** How much of the grab `part` the rider is showing (0..1, eased). */
  private part(part: SkatePart): number {
    return this.grab.of(part);
  }
}

/** Swings a leg forward (or back, negative) at the hip, the foot turned back to stay level. */
function kickForward(leg: Leg, a: number): void {
  if (a === 0) return;
  leg.hip.rotation.z -= a;
  leg.foot.rotation.z += a;
}

/** Turns the rider by `angle` about `axis` (the yaw group's space) through the hips. */
function spinAboutHips(rig: SkaterRig, axis: THREE.Vector3, angle: number, hipY: number): void {
  const r = TMP_Q2.setFromAxisAngle(axis, angle);
  const hips = TMP_V2.set(0, hipY, 0).applyQuaternion(rig.rider.quaternion).add(rig.rider.position);
  rig.rider.position.sub(hips).applyQuaternion(r).add(hips);
  rig.rider.quaternion.premultiply(r);
}

/**
 * The axes (the yaw group's space: +z the way you roll, +x your side) the
 * body turns about for each □ trick's animation id (`sim/rides.ts`): a
 * flatspin is a backflip laid over flat, a misty a forward flip thrown out
 * to the side, a rodeo a backflip thrown the other way, a corkscrew a
 * barrel roll, a bio flip a backflip on a diagonal.
 */
const SKATE_SPINS: Readonly<Partial<Record<FlipAnim, THREE.Vector3>>> = {
  kickflip: new THREE.Vector3(1, 0.9, 0).normalize(),
  varial: new THREE.Vector3(-0.7, 0.3, 0.7).normalize(),
  heelflip: new THREE.Vector3(1, -0.4, -0.7).normalize(),
  impossible: new THREE.Vector3(0, 0.25, 1).normalize(),
  varialheel: new THREE.Vector3(1, 0.3, 0.6).normalize(),
};
/** A hop out of a manual spins flat. */
const HOP_SPIN = new THREE.Vector3(0, 1, 0);

/**
 * How the rider stands on the rail for each soul plate trick (#140), keyed
 * by the grind style the ride's table gives it. `yaw` turns the rider from
 * facing along the rail (positive: chest to the rail's left), `front` and
 * `back` move each foot along the rider's facing, `spread` pushes the feet
 * apart, `lift` raises the back foot off the rail (Makio), `roll` turns the
 * front foot over onto its top (Topsoul), `cross` crosses the back skate
 * behind (Royale, Unity), `crouch` and `lean` sink and fold the body.
 */
interface SoulPose {
  yaw: number;
  front: number;
  back: number;
  spread: number;
  lift: number;
  roll: number;
  cross: number;
  crouch: number;
  lean: number;
}

const SOUL = { yaw: 0, front: 0, back: 0, spread: 0, lift: 0, roll: 0, cross: 0, crouch: 0.12, lean: 0.1 } as const;
const SOUL_POSES: Readonly<Partial<Record<GrindStyle, SoulPose>>> = {
  // Side-on, the front skate's plate along the rail, the back skate's frame across it.
  fiftyfifty: { ...SOUL, yaw: 1.2, spread: 0.08 },
  // The Soul with the front foot rolled over onto its top.
  nosegrind: { ...SOUL, yaw: 1.2, spread: 0.08, roll: 0.7 },
  // Facing down the rail on one plate, the other foot held up.
  fiveo: { ...SOUL, yaw: 0.2, lift: 0.6, crouch: 0.18 },
  // Both frames across the rail, chest to it.
  boardslide: { ...SOUL, yaw: Math.PI / 2, crouch: 0.16, lean: 0.25 },
  // Both frames across the rail, back to it.
  lipslide: { ...SOUL, yaw: -Math.PI / 2, crouch: 0.16, lean: 0.25 },
  // The back skate crossed behind the front.
  crooked: { ...SOUL, yaw: 1.3, cross: 0.25 },
  // The back skate crossed in front, ankles locked.
  overcrook: { ...SOUL, yaw: 1.3, cross: -0.3, crouch: 0.2 },
  // The plate on the back foot, turned out, the front skate ahead.
  smith: { ...SOUL, yaw: 1.0, front: 0.12, back: -0.08 },
  // Backside, a plate on each foot, knees in.
  feeble: { ...SOUL, yaw: -1.2, spread: 0.05, crouch: 0.2 },
};

/** The grabs `apply` knows how to pose, by name; `skates-pose.test.ts` checks every skate grab is one. */
export const SKATE_POSE_PARTS = ["Mute", "Japan", "Stale", "Rocket", "Liu Kang"] as const;
export type SkatePart = (typeof SKATE_POSE_PARTS)[number];

/** Whether a grab is one `apply` poses. */
export function hasSkateGrab(name: string): boolean {
  return (SKATE_POSE_PARTS as readonly string[]).includes(name);
}

/** Whether a grind style has its own soul plate stance (otherwise it's a Soul). */
export function hasSoulPose(style: GrindStyle): boolean {
  return Object.hasOwn(SOUL_POSES, style);
}

/** Whether a flip id has its own way of throwing the body round. */
export function hasSkateSpin(anim: FlipAnim): boolean {
  return Object.hasOwn(SKATE_SPINS, anim);
}

/** The hips over the skates, knees soft (m). */
const STAND_HIP = 0.64;
/** The feet a little apart along the way you roll (m). */
const FOOT_STAGGER = 0.06;
/** On a rail the plates are under the arch, a little lower than the wheels. */
const GRIND_LIFT = -0.04;
/** The stride's pace (radians a second): a push each side about every second. */
const STRIDE_RATE = 5.5;
/** Slung (#145): the laces this far above the front shoulder's joint (m). */
const SLING_UP = 0.06;
const TMP_S = new THREE.Vector3();
const ONE = new THREE.Vector3(1, 1, 1);
const TMP_M = new THREE.Matrix4();
const TMP_M2 = new THREE.Matrix4();
const TMP_Q = new THREE.Quaternion();
const TMP_Q2 = new THREE.Quaternion();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const TMP_V = new THREE.Vector3();
const TMP_V2 = new THREE.Vector3();
